// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Outlet, useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import { Algorithm } from '../config/config';
import { useIdpInfo } from '../contexts/IdpContext';
import { useUserState } from '../contexts/UserContext';
import { useConfig } from '../hooks/other/useConfig';

export const AuthCheck: React.FC = () => {
  const { loggedInUser, login } = useUserState();
  const { idpInfo } = useIdpInfo();
  const config = useConfig();
  const auth = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (config.app.mode === 'utility' && idpInfo?.algorithm === Algorithm.RS256) {
      // Don't do any redirects if the oidcAuth is still loading.
      if (auth.isLoading) {
        return;
      }
      if (!auth.isAuthenticated) {
        // only redirect if user is not authenticated
        nav('/login');
      }

      return;
    }

    if (!loggedInUser && idpInfo?.algorithm === Algorithm.RS256) {
      login();
    } else if (!loggedInUser && idpInfo?.algorithm === 'idp-not-set') {
      nav('/login');
    } else if (!loggedInUser) {
      nav('/login');
    }
  }, [auth, config, loggedInUser, idpInfo, nav, login]);

  const waitForOauth =
    idpInfo?.algorithm === Algorithm.RS256 && auth?.isLoading && !auth?.isAuthenticated;

  if (waitForOauth || !idpInfo || !loggedInUser) {
    return <Loading text="Loading user..." />;
  }

  return <Outlet />;
};
